// FieldIQ — BuildIQ crew work hub (the construction vertical's work_log tab).
// Execution-only against the BuildIQ crew API: today's phases with status
// flips, time clock, checklists + tasks, and job-site photos. Pricing never
// reaches this tab — the server redacts it before it leaves the building.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  CheckSquare,
  ClipboardList,
  Clock,
  HardHat,
  Loader2,
  Play,
  RefreshCw,
  Square,
} from "lucide-react";
import {
  biqCrewPush,
  biqGetState,
  biqListPhotos,
  biqPhotoUrl,
  biqUid,
  biqUploadPhoto,
  compressPhoto,
} from "../lib/buildiq";

const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const PHASE_NEXT = {
  scheduled: "in progress",
  confirmed: "in progress",
  "in progress": "done",
};
const PHASE_LABEL = {
  scheduled: "Start",
  confirmed: "Start",
  "in progress": "Mark done",
};

export const BuildIQWorkTab = ({ user, isOnline }) => {
  const [db, setDb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const who = user?.first_name || user?.name || user?.email || "crew";

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const d = await biqGetState();
    if (d.error) setErr(d.error);
    else setDb(d);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const jobs = useMemo(() => {
    if (!db) return [];
    const order = Array.isArray(db.order) ? db.order : [];
    const ids = [...order, ...Object.keys(db.jobs || {})].filter(
      (id, i, a) => db.jobs?.[id] && a.indexOf(id) === i,
    );
    return ids
      .map((id) => db.jobs[id])
      .filter((j) => j && ["won", "progress"].includes(j.status));
  }, [db]);

  const job = useMemo(
    () => jobs.find((j) => j.id === activeJob) || jobs[0] || null,
    [jobs, activeJob],
  );

  useEffect(() => {
    if (!job) return;
    biqListPhotos(job.id).then((d) => setPhotos(d.photos || []));
  }, [job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push one job's crew-editable fields, stamped, then refresh local state.
  const push = async (j, patch, key) => {
    setBusyKey(key);
    const updated = { ...j, ...patch, updated: Date.now() };
    const res = await biqCrewPush({ [j.id]: updated });
    if (res.error) setErr(res.error);
    else
      setDb((prev) => ({ ...prev, jobs: { ...prev.jobs, [j.id]: updated } }));
    setBusyKey(null);
  };

  const flipPhase = (j, phase) => {
    const next = PHASE_NEXT[phase.status];
    if (!next) return;
    const schedule = {
      ...j.schedule,
      phases: (j.schedule?.phases || []).map((p) =>
        p === phase ? { ...p, status: next } : p,
      ),
    };
    push(j, { schedule }, `ph-${phase.id || phase.trade}`);
  };

  const myOpenEntry = (j) =>
    (j.time || []).find((e) => e && e.who === who && e.start && !e.end);

  const clock = (j) => {
    const open = myOpenEntry(j);
    const time = open
      ? (j.time || []).map((e) =>
          e === open ? { ...e, end: new Date().toISOString() } : e,
        )
      : [
          ...(j.time || []),
          { id: biqUid(), who, start: new Date().toISOString(), end: "" },
        ];
    const log = [
      {
        at: new Date().toISOString(),
        type: "time",
        text: `${open ? "⏹" : "▶"} ${who} clocked ${open ? "out" : "in"}`,
      },
    ];
    push(j, { time, log: [...(j.log || []), ...log] }, `clock-${j.id}`);
  };

  const toggleTask = (j, task) => {
    const tasks = (j.tasks || []).map((t) =>
      t === task ? { ...t, done: !t.done } : t,
    );
    push(j, { tasks }, `task-${task.id || task.text}`);
  };

  const toggleCheckItem = (j, list, item) => {
    const checklists = (j.checklists || []).map((cl) =>
      cl !== list
        ? cl
        : {
            ...cl,
            items: (cl.items || []).map((it) =>
              it === item
                ? {
                    ...it,
                    done: !it.done,
                    doneAt: it.done ? "" : new Date().toISOString(),
                  }
                : it,
            ),
          },
    );
    push(j, { checklists }, `ck-${item.id || item.text}`);
  };

  const uploadPhoto = async (file) => {
    if (!file || !job) return;
    setUploading(true);
    setErr(null);
    try {
      const { imgB64, thumbB64, w, h } = await compressPhoto(file);
      const gps = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 4000 },
        );
      });
      const r = await biqUploadPhoto(
        job.id,
        { note: "", tags: [], gps, w, h },
        imgB64,
        thumbB64,
      );
      if (r.error) setErr(r.error);
      else {
        const d = await biqListPhotos(job.id);
        setPhotos(d.photos || []);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-48 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading jobs…
      </div>
    );

  if (!jobs.length)
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-500">
        <HardHat size={36} />
        <p className="text-sm font-medium">No active jobs assigned</p>
        {err && <p className="text-xs text-red-400">{err}</p>}
        <button onClick={load} className="text-xs text-slate-400 underline">
          Refresh
        </button>
      </div>
    );

  const today = todayISO();
  const activePhases = (job?.schedule?.phases || []).filter(
    (p) => p && p.status !== "done" && (p.trade || p.label),
  );
  const todayPhases = activePhases.filter(
    (p) => p.start && p.start <= today && (!p.end || p.end >= today),
  );
  const upcoming = activePhases
    .filter((p) => p.start && p.start > today)
    .slice(0, 3);
  const open = job ? myOpenEntry(job) : null;

  return (
    <div className="space-y-4">
      {err && (
        <div className="text-xs text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {/* job selector */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {jobs.map((j) => (
          <button
            key={j.id}
            onClick={() => setActiveJob(j.id)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${
              job?.id === j.id
                ? "bg-amber-500 border-amber-500 text-slate-900"
                : "border-slate-700 text-slate-300"
            }`}
          >
            {j.name}
          </button>
        ))}
        <button
          onClick={load}
          className="shrink-0 px-2 py-1.5 rounded-full border border-slate-700 text-slate-400"
          title="Refresh"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {job && (
        <>
          {/* time clock */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                <Clock size={15} /> Time clock
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {open
                  ? `Clocked in since ${new Date(open.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                  : "Not clocked in"}
              </p>
            </div>
            <button
              onClick={() => clock(job)}
              disabled={busyKey === `clock-${job.id}` || !isOnline}
              className={`px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 ${
                open ? "bg-red-600 text-white" : "bg-green-600 text-white"
              }`}
            >
              {busyKey === `clock-${job.id}`
                ? "…"
                : open
                  ? "Clock out"
                  : "Clock in"}
            </button>
          </div>

          {/* today's phases */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              <HardHat size={15} /> Today at {job.name}
            </p>
            {todayPhases.length === 0 && (
              <p className="text-xs text-slate-500">
                No phases scheduled today.
              </p>
            )}
            {todayPhases.map((p, i) => (
              <div
                key={p.id || i}
                className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2"
              >
                <div>
                  <p className="text-sm text-slate-200">{p.label || p.trade}</p>
                  <p className="text-[11px] text-slate-500">
                    {p.start}
                    {p.end && p.end !== p.start ? ` → ${p.end}` : ""}
                    {p.sub ? ` · ${p.sub}` : ""} · {p.status}
                  </p>
                </div>
                {PHASE_NEXT[p.status] && (
                  <button
                    onClick={() => flipPhase(job, p)}
                    disabled={busyKey === `ph-${p.id || p.trade}` || !isOnline}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-slate-900 disabled:opacity-50"
                  >
                    {p.status === "in progress" ? (
                      <Square size={12} />
                    ) : (
                      <Play size={12} />
                    )}
                    {PHASE_LABEL[p.status]}
                  </button>
                )}
              </div>
            ))}
            {upcoming.length > 0 && (
              <p className="text-[11px] text-slate-500 pt-1">
                Next up:{" "}
                {upcoming
                  .map((p) => `${p.label || p.trade} (${p.start})`)
                  .join(" · ")}
              </p>
            )}
          </div>

          {/* tasks */}
          {(job.tasks || []).length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-1.5">
              <p className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                <ClipboardList size={15} /> Tasks
              </p>
              {(job.tasks || []).map((t, i) => (
                <button
                  key={t.id || i}
                  onClick={() => toggleTask(job, t)}
                  disabled={!isOnline}
                  className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-lg hover:bg-slate-800/60"
                >
                  <CheckSquare
                    size={15}
                    className={t.done ? "text-green-500" : "text-slate-600"}
                  />
                  <span
                    className={`text-sm ${t.done ? "text-slate-500 line-through" : "text-slate-200"}`}
                  >
                    {t.text}
                  </span>
                  {t.due && (
                    <span className="ml-auto text-[11px] text-slate-500">
                      {t.due}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* checklists */}
          {(job.checklists || []).map((cl, ci) => (
            <div
              key={cl.id || ci}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-1.5"
            >
              <p className="text-sm font-semibold text-slate-200">{cl.name}</p>
              {(cl.items || []).map((it, ii) => (
                <button
                  key={it.id || ii}
                  onClick={() => toggleCheckItem(job, cl, it)}
                  disabled={!isOnline}
                  className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-lg hover:bg-slate-800/60"
                >
                  <CheckSquare
                    size={15}
                    className={it.done ? "text-green-500" : "text-slate-600"}
                  />
                  <span
                    className={`text-sm ${it.done ? "text-slate-500 line-through" : "text-slate-200"}`}
                  >
                    {it.text}
                  </span>
                </button>
              ))}
            </div>
          ))}

          {/* photos */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                <Camera size={15} /> Photos
              </p>
              <label
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-700 text-slate-200 cursor-pointer ${uploading || !isOnline ? "opacity-50 pointer-events-none" : ""}`}
              >
                {uploading ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Camera size={13} />
                )}
                {uploading ? "Uploading…" : "Add photo"}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                />
              </label>
            </div>
            {photos.length === 0 ? (
              <p className="text-xs text-slate-500">
                No photos yet for this job.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.slice(0, 12).map((p) => (
                  <a
                    key={p.id}
                    href={biqPhotoUrl(p.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="block aspect-square rounded-lg overflow-hidden bg-slate-800"
                  >
                    <img
                      src={biqPhotoUrl(p.id, true)}
                      alt={p.note || "job site"}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
